
import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CanAccessDirective } from '@core';
import { MODULE_RESOURCES, moduleAccess, moduleAccessAny, userSelfAccess } from '@core/config/module-access';
import { AuthorizationRequirement, CenterAccess } from '@core/models/interface';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { FeatherIconsComponent } from '@shared/components/feather-icons/feather-icons.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-user-profile-menu',
  templateUrl: './user-profile-menu.component.html',
  styleUrls: ['./user-profile-menu.component.scss'],
  imports: [
    NgClass,
    CanAccessDirective,
    MatMenuModule,
    FeatherIconsComponent,
    MatButtonModule,
    TranslateModule
  ],
})
export class UserProfileMenuComponent {
  isSettingsCollapsed = true;
  isCentersCollapsed = true;
  readonly accountAccess = userSelfAccess();
  readonly inboxAccess = moduleAccess(MODULE_RESOURCES.header.userProfile.inbox);
  readonly modulosSettingsAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.modules
  );
  readonly centersSettingsAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.centers
  );
  readonly rolesSettingsAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.roles
  );
  readonly createPermissAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.permissions
  );
  readonly notificationsSettingsAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.notifications
  );
  readonly userAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.users
  );
  readonly systemUpdatesAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.systemUpdates
  );

  readonly settingsAccess: AuthorizationRequirement = moduleAccessAny([
    MODULE_RESOURCES.header.userProfile.settings.centers,
    MODULE_RESOURCES.header.userProfile.settings.permissions,
    MODULE_RESOURCES.header.userProfile.settings.roles,
    MODULE_RESOURCES.header.userProfile.settings.modules,
    MODULE_RESOURCES.header.userProfile.settings.notifications,
    MODULE_RESOURCES.header.userProfile.settings.users,
    MODULE_RESOURCES.header.userProfile.settings.systemUpdates
  ]);
  @Input() userName: string = '';
  @Input() userImg: string = '';
  @Input() showCentersEntry = false;
  @Input() currentCenter = '';
  @Input() currentCenterName = '';
  @Input() availableCenters: CenterAccess[] = [];

  get userInitials(): string {
    return this.buildInitials(this.userName);
  }

  @Output() accountClicked = new EventEmitter<void>();
  @Output() inboxClicked = new EventEmitter<void>();
  @Output() moduleSettingsClicked = new EventEmitter<void>();
  @Output() centersSettingsClicked = new EventEmitter<void>();
  @Output() rolesSettingsClicked = new EventEmitter<void>();
  @Output() usersSettingsClicked = new EventEmitter<void>();
  @Output() systemUpdatesSettingsClicked = new EventEmitter<void>();
  @Output() notificationsSettingsClicked = new EventEmitter<void>();
  @Output() createPermissAccessClicked = new EventEmitter<void>();
  @Output() centerSelected = new EventEmitter<string>();
  @Output() logoutClicked = new EventEmitter<void>();

  onAccountClick() {
    this.accountClicked.emit();
  }
  onInboxClick() {
    this.inboxClicked.emit();
  }
  toggleSettings(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.isSettingsCollapsed = !this.isSettingsCollapsed;
  }
  toggleCenters(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.isCentersCollapsed = !this.isCentersCollapsed;
  }
  onModulosClick() {
    this.moduleSettingsClicked.emit();
  }
  onCentersClick() {
    this.centersSettingsClicked.emit();
  }
  onSelectCenter(center: CenterAccess) {
    this.centerSelected.emit(center.center);
  }
  onCreatePermissAccessClick() {
    this.createPermissAccessClicked.emit();
  }
  onNotificationsSettingsClick() {
    this.notificationsSettingsClicked.emit();
  }
  onRolesClick() {
    this.rolesSettingsClicked.emit();
  }
  onUsersClick() {
    this.usersSettingsClicked.emit();
  }
  onSystemUpdatesClick() {
    this.systemUpdatesSettingsClicked.emit();
  }
  onLogoutClick() {
    this.logoutClicked.emit();
  }

  private buildInitials(value: string): string {
    return value
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('') || 'US';
  }
}
